import { defineStore } from 'pinia'
import axios from '@/services/axios'
import notificationService from '@/services/notificationService'
import passkeyService from '@/services/passkeyService'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('token') || sessionStorage.getItem('token'),
    refreshToken: localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken'),
    isLoading: false,
    branch: null,
    branchAccess: [], // For HR users with multi-branch access
    companyAccess: [], // For users restricted to specific companies
    permissions: [],
    rememberMe: localStorage.getItem('rememberMe') === 'true', // Track remember me preference
    // Request deduplication flags
    isFetchingProfile: false,
    isRefreshingToken: false,
    isInitializing: false,
    lastProfileFetch: null,
    profileCacheTTL: 60000, // 1 minute cache
    // WebSocket connection status
    isWebSocketConnected: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    fullName: (state) => state.user ? `${state.user.first_name} ${state.user.last_name}` : '',
    userRole: (state) => state.user?.role || null,
    branchId: (state) => state.branch?.id || null,
    branchName: (state) => state.branch?.name || '',
  },

  actions: {
    // Helper function for exponential backoff retry
    async retryWithBackoff(fn, maxRetries = 3) {
      for (let i = 0; i < maxRetries; i++) {
        try {
          return await fn()
        } catch (error) {
          // If it's a 429 error and not the last retry, wait and try again
          if (error.response?.status === 429 && i < maxRetries - 1) {
            const delay = Math.min(1000 * Math.pow(2, i), 8000) // Max 8 seconds
            console.log(`⏳ Rate limited (429). Retrying in ${delay}ms...`)
            await new Promise(resolve => setTimeout(resolve, delay))
            continue
          }
          // For other errors or last retry, throw the error
          throw error
        }
      }
    },

    async login(credentials) {
      this.isLoading = true
      try {
        const response = await axios.post('/auth/login', credentials)
        const { user, token, refreshToken, branch, branchAccess, companyAccess, permissions } = response.data

        this.user = user
        this.token = token
        this.refreshToken = refreshToken
        this.branch = branch
        this.branchAccess = branchAccess || []
        this.companyAccess = companyAccess || []
        this.permissions = permissions
        this.rememberMe = credentials.remember || false

        // Store tokens based on "remember me" preference
        const storage = this.rememberMe ? localStorage : sessionStorage

        // Clear tokens from both storages first
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('refreshToken')

        // Store in the appropriate storage
        storage.setItem('token', token)
        storage.setItem('refreshToken', refreshToken)

        // Store remember preference in localStorage (always persistent)
        if (this.rememberMe) {
          localStorage.setItem('rememberMe', 'true')
        } else {
          localStorage.removeItem('rememberMe')
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        // Cache user data for offline access
        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem('branch', JSON.stringify(branch))

        // Connect to WebSocket after successful login
        await this.connectWebSocket()

        return {
          success: true,
          must_change_password: user.must_change_password || false
        }
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || 'เข้าสู่ระบบไม่สำเร็จ'
        }
      } finally {
        this.isLoading = false
      }
    },

    async loginWithPasskey(email = null) {
      this.isLoading = true
      try {
        const data = await passkeyService.authenticate(email)
        const { user, token, refreshToken, branch, branchAccess, companyAccess, permissions } = data

        this.user = user
        this.token = token
        this.refreshToken = refreshToken
        this.branch = branch
        this.branchAccess = branchAccess || []
        this.companyAccess = companyAccess || []
        this.permissions = permissions
        this.rememberMe = true // passkey login always persists

        // Clear tokens from both storages first
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('refreshToken')

        // Store in localStorage (persistent)
        localStorage.setItem('token', token)
        localStorage.setItem('refreshToken', refreshToken)
        localStorage.setItem('rememberMe', 'true')

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        // Cache user data for offline access
        localStorage.setItem('user', JSON.stringify(user))
        localStorage.setItem('branch', JSON.stringify(branch))

        // Connect to WebSocket after successful login
        await this.connectWebSocket()

        return {
          success: true,
          must_change_password: user.must_change_password || false
        }
      } catch (error) {
        // NotAllowedError means user cancelled the prompt
        if (error.name === 'NotAllowedError') {
          return { success: false, message: 'ยกเลิกการยืนยันตัวตน', cancelled: true }
        }
        return {
          success: false,
          message: error.response?.data?.error || error.message || 'Passkey authentication failed'
        }
      } finally {
        this.isLoading = false
      }
    },

    async logout() {
      // Prevent concurrent logout calls
      if (this.isLoading && !this.token) {
        return // Already logging out
      }

      this.isLoading = true

      try {
        // Only call logout API if we have a token
        if (this.token) {
          await axios.post('/auth/logout', {})
        }
      } catch (error) {
        // Ignore logout API errors (including 429) - still proceed with local cleanup
        if (error.response?.status === 429) {
          console.warn('Logout API rate limited (429) - proceeding with local cleanup')
        } else {
          console.warn('Logout API failed (continuing with local cleanup):', error.message)
        }
      }

      // Disconnect WebSocket before cleaning up
      this.disconnectWebSocket()

      // Always clean up local state regardless of API response
      this.user = null
      this.token = null
      this.refreshToken = null
      this.branch = null
      this.branchAccess = []
      this.companyAccess = []
      this.permissions = []
      this.rememberMe = false
      this.lastProfileFetch = null
      this.isFetchingProfile = false
      this.isRefreshingToken = false
      this.isInitializing = false
      this.isLoading = false
      this.isWebSocketConnected = false

      // Clear tokens and cached data from both storages
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('rememberMe')
      localStorage.removeItem('user')
      localStorage.removeItem('branch')
      sessionStorage.removeItem('token')
      sessionStorage.removeItem('refreshToken')
      delete axios.defaults.headers.common['Authorization']
    },

    async refreshAuthToken() {
      if (!this.refreshToken) return false

      // Prevent concurrent refresh calls
      if (this.isRefreshingToken) {
        console.log('🔄 Token refresh already in progress, waiting...')
        // Wait for the current refresh to complete
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.isRefreshingToken) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        })
        return !!this.token
      }

      this.isRefreshingToken = true

      try {
        const response = await this.retryWithBackoff(async () => {
          return await axios.post('/auth/refresh', {
            refreshToken: this.refreshToken
          })
        })

        const { token, refreshToken } = response.data
        this.token = token
        this.refreshToken = refreshToken

        // Store in the same storage as login (based on rememberMe)
        const storage = this.rememberMe ? localStorage : sessionStorage
        storage.setItem('token', token)
        storage.setItem('refreshToken', refreshToken)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        this.isRefreshingToken = false
        return true
      } catch (error) {
        this.isRefreshingToken = false

        // Don't logout on 429 errors
        if (error.response?.status === 429) {
          console.warn('Token refresh rate limited (429), will retry later')
          return false
        }

        // Only logout on auth errors (401, 403)
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.logout()
        }
        return false
      }
    },

    async fetchProfile(forceRefresh = false) {
      // Check cache first (unless force refresh)
      if (!forceRefresh && this.lastProfileFetch) {
        const timeSinceLastFetch = Date.now() - this.lastProfileFetch
        if (timeSinceLastFetch < this.profileCacheTTL) {
          console.log('📦 Using cached profile data')
          return true
        }
      }

      // Prevent concurrent fetch calls
      if (this.isFetchingProfile) {
        console.log('🔄 Profile fetch already in progress, waiting...')
        // Wait for the current fetch to complete
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.isFetchingProfile) {
              clearInterval(checkInterval)
              resolve()
            }
          }, 100)
        })
        return !!this.user
      }

      this.isFetchingProfile = true

      try {
        const response = await this.retryWithBackoff(async () => {
          return await axios.get('/auth/profile')
        })

        // Preserve must_change_password flag from login if profile doesn't include it
        const mustChangePassword = this.user?.must_change_password
        this.user = response.data.user
        if (mustChangePassword && !this.user.must_change_password) {
          this.user.must_change_password = mustChangePassword
        }
        this.branch = response.data.branch
        this.branchAccess = response.data.branchAccess || []
        this.companyAccess = response.data.companyAccess || []
        this.permissions = response.data.permissions
        this.lastProfileFetch = Date.now()
        this.isFetchingProfile = false

        // Cache user data for offline access
        localStorage.setItem('user', JSON.stringify(this.user))
        localStorage.setItem('branch', JSON.stringify(this.branch))

        return true
      } catch (error) {
        this.isFetchingProfile = false
        console.error('Failed to fetch profile:', error)

        // Don't logout on 429 errors
        if (error.response?.status === 429) {
          console.warn('Profile fetch rate limited (429), will use cached data if available')
          return !!this.user
        }

        // If offline (no response = network error), restore from cache
        if (!error.response && !navigator.onLine) {
          console.log('Offline: restoring user data from cache')
          return this._restoreFromCache()
        }

        // Only logout on authentication errors (401, 403), not server errors (5xx)
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          this.logout()
        }
        return false
      }
    },

    // Restore user data from localStorage cache (for offline access)
    _restoreFromCache() {
      try {
        const cachedUser = localStorage.getItem('user')
        const cachedBranch = localStorage.getItem('branch')
        if (cachedUser) {
          this.user = JSON.parse(cachedUser)
          this.branch = cachedBranch ? JSON.parse(cachedBranch) : null
          console.log('Restored user from offline cache:', this.user?.id)
          return true
        }
      } catch (e) {
        console.warn('Failed to restore from cache:', e)
      }
      return false
    },

    // Initialize auth state on page load
    async initializeAuth() {
      // Prevent concurrent initialization
      if (this.isInitializing) {
        console.log('⏳ Auth initialization already in progress')
        return
      }

      if (!this.token) {
        return
      }

      this.isInitializing = true

      try {
        // Set axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`

        // If offline, restore from cache immediately
        if (!navigator.onLine) {
          console.log('Offline: restoring auth from cache')
          this._restoreFromCache()
          this.isInitializing = false
          return
        }

        // Try to fetch profile
        const success = await this.fetchProfile()
        if (!success && this.token) {
          // If profile fetch fails (and we still have a token), try to refresh token
          const refreshed = await this.refreshAuthToken()
          if (refreshed) {
            // Only fetch profile again if refresh was successful
            await this.fetchProfile()
          }
        }

        // Connect to WebSocket if authenticated
        if (this.isAuthenticated && !this.isWebSocketConnected) {
          await this.connectWebSocket()
        }
      } finally {
        this.isInitializing = false
      }
    },

    // Force refresh user data and branchAccess
    async forceRefreshUserData() {
      console.log('🔄 Force refreshing user data...')
      const success = await this.fetchProfile(true) // Pass forceRefresh=true
      if (success) {
        console.log('✅ Force refresh complete - branchAccess:', this.branchAccess)
      } else {
        console.error('❌ Force refresh failed')
      }
      return success
    },

    // Switch branch for HR users (updates JWT with new branch)
    async switchBranch(branchId) {
      try {
        console.log('🔄 Switching to branch:', branchId)

        // Call switch branch API
        const response = await axios.post('/auth/switch-branch', {
          branchId: parseInt(branchId)
        })

        // Update store with new data
        const { user, token, refreshToken, branch, branchAccess, companyAccess } = response.data

        this.user = user
        this.token = token
        this.refreshToken = refreshToken
        this.branch = branch
        this.branchAccess = branchAccess || []
        this.companyAccess = companyAccess || []

        // Store in the same storage as login (based on rememberMe)
        const storage = this.rememberMe ? localStorage : sessionStorage
        storage.setItem('token', token)
        storage.setItem('refreshToken', refreshToken)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        console.log('✅ Branch switched successfully to:', branch.name)
        return true
      } catch (error) {
        console.error('❌ Branch switch failed:', error)
        return false
      }
    },

    hasRole(role) {
      if (Array.isArray(role)) {
        return role.includes(this.user?.role)
      }
      return this.user?.role === role
    },

    hasPermission(permission) {
      return this.permissions.includes(permission)
    },

    canAccessBranch(branchId) {
      if (this.user?.role === 'super_admin') return true
      if (this.user?.role === 'hr' && this.branchAccess.length > 0) {
        return this.branchAccess.some(access => access.branch_id === branchId)
      }
      return this.branchId === branchId
    },

    // Update avatar URL after self-service upload
    updateAvatarUrl(url) {
      if (this.user) {
        this.user.avatar_url = url
        localStorage.setItem('user', JSON.stringify(this.user))
      }
    },

    // Clear must_change_password flag after password change
    clearMustChangePassword() {
      if (this.user) {
        this.user.must_change_password = false
      }
    },

    // WebSocket connection management
    async connectWebSocket() {
      if (!this.token || this.isWebSocketConnected) {
        return
      }

      try {
        console.log('🔌 Connecting to WebSocket...')
        await notificationService.connect(this.token)
        this.isWebSocketConnected = true
        console.log('✅ WebSocket connected successfully')
      } catch (error) {
        console.error('❌ WebSocket connection failed:', error)
        this.isWebSocketConnected = false
      }
    },

    disconnectWebSocket() {
      if (this.isWebSocketConnected) {
        console.log('🔌 Disconnecting from WebSocket...')
        notificationService.disconnect()
        this.isWebSocketConnected = false
      }
    },

    // Switch branch for HR users with multi-branch access
    async switchBranch(branchId) {
      console.log('🔄 switchBranch called with branchId:', branchId)
      console.log('🔍 Current branchAccess:', this.branchAccess)
      console.log('🔍 canAccessBranch result:', this.canAccessBranch(branchId))

      if (!this.canAccessBranch(branchId)) {
        console.error('❌ Access denied to branch:', branchId)
        throw new Error('ไม่มีสิทธิ์เข้าสาขานี้')
      }

      try {
        console.log('🔄 Calling switch-branch API with:', { branch_id: branchId })
        const response = await axios.post('/auth/switch-branch', { branch_id: branchId })
        console.log('✅ Switch branch API response:', response.data)

        const { user, token, refreshToken, branch, companyAccess } = response.data

        this.user = user
        this.token = token
        this.refreshToken = refreshToken
        this.branch = branch
        this.companyAccess = companyAccess || []

        // Store in the same storage as login (based on rememberMe)
        const storage = this.rememberMe ? localStorage : sessionStorage
        storage.setItem('token', token)
        storage.setItem('refreshToken', refreshToken)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        console.log('✅ Auth store updated, new branch:', this.branch)
        return { success: true }
      } catch (error) {
        console.error('❌ Switch branch API error:', error)
        return {
          success: false,
          message: error.response?.data?.message || 'ไม่สามารถเปลี่ยนสาขาได้'
        }
      }
    }
  }
})